/**
 * Per route SEO data for the five marketing pages.
 *
 * Everything here is a module level constant on purpose: the objects are passed
 * straight into the Seo component's effect dependency list, so a stable identity
 * keeps the effect from re-running on every render.
 *
 * FAQ answers are copied verbatim from the visible FAQ blocks on each page.
 * Search engines require the marked up answer to match what a visitor reads, so
 * if you edit a FAQ on a page you must edit the matching entry here too.
 */

export const SITE = 'https://axiomsquarepk.com';
export const ORG_ID = SITE + '/#organization';
export const SITE_ID = SITE + '/#website';
export const DEFAULT_IMAGE = SITE + '/marketing/images/og-image.png';

const url = (path) => SITE + path;

const faqPage = (pageUrl, items) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  '@id': pageUrl + '#faq',
  mainEntity: items.map((it) => ({
    '@type': 'Question',
    name: it.q,
    acceptedAnswer: { '@type': 'Answer', text: it.a },
  })),
});

const breadcrumbs = (trail) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: trail.map((t, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: t.name,
    item: t.url,
  })),
});

const howTo = (pageUrl, name, description, steps) => ({
  '@context': 'https://schema.org',
  '@type': 'HowTo',
  '@id': pageUrl + '#howto',
  name,
  description,
  step: steps.map((s, i) => ({
    '@type': 'HowToStep',
    position: i + 1,
    name: s.name,
    text: s.text,
  })),
});

const webPage = (pageUrl, name, description) => ({
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': pageUrl + '#webpage',
  url: pageUrl,
  name,
  description,
  inLanguage: 'en-PK',
  isPartOf: { '@id': SITE_ID },
  about: { '@id': ORG_ID },
  publisher: { '@id': ORG_ID },
});

const service = (pageUrl, data) => ({
  '@context': 'https://schema.org',
  '@type': 'Service',
  '@id': pageUrl + '#service',
  url: pageUrl,
  provider: { '@id': ORG_ID },
  areaServed: data.areaServed,
  ...data.fields,
});

/* ------------------------------------------------------------------ */
/* Home                                                                */
/* ------------------------------------------------------------------ */

const HOME_URL = url('/');

const HOME_FAQ = [
  {
    q: 'Do I need all four services?',
    a: 'No. Most clients start with one, usually FBR or PRA invoicing, or a single tax return, and add the others when it makes sense.',
  },
  {
    q: 'How quickly can I be compliant?',
    a: 'Invoicing setup usually takes a few days once your business details and FBR registration are in order. Salaried tax returns are typically filed within a week of receiving documents.',
  },
  {
    q: 'Do I need both FBR and PRA invoicing?',
    a: 'It depends on what you sell and where. Goods generally fall under FBR, services billed in Punjab fall under PRA, and some businesses need both. We can confirm your case on a call.',
  },
  {
    q: 'Is my financial information kept confidential?',
    a: 'Yes. Documents are seen only by the consultant assigned to your case and are not shared outside Axiom Square.',
  },
  {
    q: 'Can I move from another POS provider?',
    a: 'Yes. Data migration and getting re registered with FBR are handled as part of onboarding, so there is no gap in your compliance.',
  },
];

/* ------------------------------------------------------------------ */
/* FBR digital invoicing                                               */
/* ------------------------------------------------------------------ */

const FBR_URL = url('/fbr-digital-invoicing');

const FBR_FAQ = [
  {
    q: 'What is FBR digital invoicing?',
    a: 'A system from the Federal Board of Revenue requiring businesses to report sales invoices through an approved integration. Each accepted invoice receives a unique reference number and carries a QR code.',
  },
  {
    q: 'Is it mandatory for my business?',
    a: 'FBR is phasing in mandatory digital invoicing by business category and turnover. We can tell you where your business currently stands on a free call.',
  },
  {
    q: 'Do I need a static IP?',
    a: 'FBR and PRAL require a whitelisted static IP before you can go live. We help arrange this during onboarding if you do not already have one.',
  },
  {
    q: 'What happens if the FBR gateway is down?',
    a: 'FBR systems occasionally have downtime outside our control. Reference data is cached locally so product lookups keep working, and failed submissions can be retried once the gateway recovers.',
  },
  {
    q: 'Can I move from another POS provider?',
    a: 'Yes. Data migration and getting re registered with FBR are handled as part of onboarding, so there is no gap in your compliance.',
  },
];

const monthlyPlan = (name, price, description) => ({
  '@type': 'Offer',
  name,
  description,
  priceSpecification: {
    '@type': 'UnitPriceSpecification',
    price,
    priceCurrency: 'PKR',
    unitText: 'month',
  },
});

/* ------------------------------------------------------------------ */
/* PRA digital invoicing                                               */
/* ------------------------------------------------------------------ */

const PRA_URL = url('/pra-digital-invoicing');

const PRA_FAQ = [
  {
    q: 'What is PRA digital invoicing?',
    a: 'A requirement from the Punjab Revenue Authority for service businesses to report invoices through an approved integration, similar in principle to FBR digital invoicing but with its own rules and portal.',
  },
  {
    q: 'Do I need this as well as FBR invoicing?',
    a: 'It depends on what you sell and where. Goods generally fall under FBR and services billed in Punjab fall under PRA. Some businesses need both, and we can confirm your case on a call.',
  },
  {
    q: 'How long does setup take?',
    a: 'Most businesses are live within a week of providing their POS ID and service details, including sandbox testing.',
  },
  {
    q: 'What happens if the PRA system is down?',
    a: 'Submissions are queued and can be retried, so a temporary outage on the PRA side does not mean lost invoices.',
  },
];

/* ------------------------------------------------------------------ */
/* Income tax filing                                                   */
/* ------------------------------------------------------------------ */

const TAX_URL = url('/tax-filing');

const TAX_FAQ = [
  {
    q: 'What documents do I need?',
    a: 'For salaried filing, your salary certificate, bank statements, and any investment or property records. We send an exact checklist after the first call.',
  },
  {
    q: 'What if I have never filed before?',
    a: 'That is common. Registering you as a filer on IRIS is part of the process and there is no separate fee for it.',
  },
  {
    q: 'Can you handle an FBR notice I already received?',
    a: 'Yes. Bring the notice to the consultation and we will explain what it means and what needs to happen next.',
  },
  {
    q: 'Is my financial information kept confidential?',
    a: 'Yes. Only the consultant assigned to your case sees your documents and nothing is shared outside Axiom Square.',
  },
  {
    q: 'How is the business quote calculated?',
    a: 'It depends on your business structure, whether sales tax returns are also needed, and your transaction volume. You get the number before committing to anything.',
  },
];

/* ------------------------------------------------------------------ */
/* Odoo integration                                                    */
/* ------------------------------------------------------------------ */

const ODOO_URL = url('/odoo-integration');

const ODOO_FAQ = [
  {
    q: 'Does this work with Odoo Community or Enterprise?',
    a: 'Both. The connector is built as a module and the approach is the same either way, though implementation details can differ.',
  },
  {
    q: 'Will this break our existing workflows?',
    a: 'No. The integration is built around your existing sales and invoicing flow rather than replacing it, and sandbox testing before go live exists specifically to confirm that.',
  },
  {
    q: 'How long does integration take?',
    a: 'It depends on your customizations and whether you need FBR, PRA, or both. Most single connector projects run two to four weeks including sandbox testing.',
  },
  {
    q: 'What happens after go live?',
    a: 'We stay available for support and maintenance, since FBR and PRA requirements do change over time.',
  },
];

const PK = { '@type': 'Country', name: 'Pakistan' };
const PUNJAB = { '@type': 'State', name: 'Punjab, Pakistan' };

/* ------------------------------------------------------------------ */
/* Exported per route configuration                                    */
/* ------------------------------------------------------------------ */

export const SEO = {
  home: {
    title: 'FBR and PRA Digital Invoicing, Tax Filing | Axiom Square',
    description:
      'Axiom Square is a Lahore based consultancy for FBR digital invoicing, PRA digital invoicing, income tax filing and Odoo integration.',
    canonical: HOME_URL,
    keywords:
      'FBR digital invoicing, PRA digital invoicing, income tax filing Pakistan, Odoo FBR integration, tax consultant Lahore, sales tax invoice software Pakistan',
    image: DEFAULT_IMAGE,
    jsonLd: [
      webPage(
        HOME_URL,
        'FBR and PRA digital invoicing, income tax filing and Odoo integration',
        'Compliance software and tax filing for Pakistani businesses, from one Lahore based team.'
      ),
      faqPage(HOME_URL, HOME_FAQ),
      howTo(HOME_URL, 'How to start working with Axiom Square',
             'The four steps from a first call to a service that is set up and supported.', [
               { name: 'Free Scoping Call', text: 'Fifteen minutes to work out what you actually need. No pitch.' },
               { name: 'Choose Your Service', text: 'Invoicing, tax filing, Odoo integration, or a combination.' },
               { name: 'We Set It Up', text: 'Registration, integration, or filing, handled by our team.' },
               { name: 'Ongoing Support', text: 'We stay available as your volume grows and the rules change.' },
             ]),
    ],
  },

  fbr: {
    title: 'FBR Digital Invoicing Software in Pakistan | Axiom Square',
    description:
      'Point of sale and invoicing software integrated with FBR digital invoicing, reporting every sale with an invoice reference number and QR code.',
    canonical: FBR_URL,
    keywords:
      'FBR digital invoicing, FBR digital invoicing software, FBR POS integration, PRAL integration, FBR invoice QR code, sales tax invoice software Pakistan, FBR compliant POS',
    image: SITE + '/marketing/images/FBR.png',
    jsonLd: [
      webPage(
        FBR_URL,
        'FBR digital invoicing software',
        'POS and invoicing software integrated with FBR digital invoicing for businesses in Pakistan.'
      ),
      breadcrumbs([
        { name: 'Home', url: HOME_URL },
        { name: 'FBR Digital Invoicing', url: FBR_URL },
      ]),
      service(FBR_URL, {
        areaServed: PK,
        fields: {
          name: 'FBR Digital Invoicing Software and POS',
          serviceType: 'FBR digital invoicing software',
          description:
            'Point of sale and invoicing software integrated with the Federal Board of Revenue digital invoicing system. Sales are submitted to FBR in real time, an invoice reference number is returned and stored, and every accepted invoice carries a QR code.',
          audience: {
            '@type': 'BusinessAudience',
            name: 'Sales tax registered retailers, wholesalers, distributors, importers and manufacturers in Pakistan',
          },
          offers: [
            monthlyPlan('Starter', '2000', '0 to 50 invoices per month'),
            monthlyPlan('Growth', '3000', '51 to 200 invoices per month'),
            monthlyPlan('Business', '5000', '201 to 500 invoices per month'),
          ],
        },
      }),
      faqPage(FBR_URL, FBR_FAQ),
      howTo(FBR_URL, 'How to get your business reporting sales to FBR digital invoicing',
             'The four steps from registration to a first sale reported to FBR.', [
               { name: 'Register Your Business', text: 'Basic business details and your FBR registration information.' },
               { name: 'We Connect You to FBR', text: 'One time integration and sandbox testing, handled by our team.' },
               { name: 'Add Your Products', text: 'Import your catalogue or add products as you go.' },
               { name: 'Start Selling', text: 'Sales report to FBR as they happen, with no extra step at checkout.' },
             ]),
    ],
  },

  pra: {
    title: 'PRA Digital Invoicing Software for Punjab | Axiom Square',
    description:
      'PRA digital invoicing for Punjab service businesses. We set up your POS ID, run sandbox testing and get your invoices reporting correctly.',
    canonical: PRA_URL,
    keywords:
      'PRA digital invoicing, Punjab Revenue Authority invoicing, PRA POS ID, PRA invoicing software, PRA e invoicing Punjab, service tax invoice Punjab',
    image: DEFAULT_IMAGE,
    jsonLd: [
      webPage(
        PRA_URL,
        'PRA digital invoicing software',
        'Punjab Revenue Authority digital invoicing for service businesses in Punjab, Pakistan.'
      ),
      breadcrumbs([
        { name: 'Home', url: HOME_URL },
        { name: 'PRA Digital Invoicing', url: PRA_URL },
      ]),
      service(PRA_URL, {
        areaServed: PUNJAB,
        fields: {
          name: 'PRA Digital Invoicing for Punjab Service Providers',
          serviceType: 'PRA digital invoicing software',
          description:
            'Invoicing software integrated with the Punjab Revenue Authority digital invoicing system for service businesses billing in Punjab. Covers POS ID setup, profile configuration, sandbox testing and go live.',
          audience: {
            '@type': 'BusinessAudience',
            name: 'Service businesses registered with the Punjab Revenue Authority',
          },
        },
      }),
      faqPage(PRA_URL, PRA_FAQ),
      howTo(PRA_URL, 'How to get a Punjab service business reporting invoices to PRA',
             'The four steps from a PRA POS ID to invoices reporting live.', [
               { name: 'Get Your PRA POS ID', text: 'We help you register with PRA if you have not already.' },
               { name: 'Set Up Your Profile', text: 'Your services, tax rates, and business details, configured once.' },
               { name: 'Sandbox Testing', text: 'A short test phase to confirm invoices report correctly before going live.' },
               { name: 'Go Live', text: 'Invoices report to PRA from that point on, with support if anything is rejected.' },
             ]),
    ],
  },

  tax: {
    title: 'Income Tax Filing in Pakistan, Flat PKR 5,000 | Axiom Square',
    description:
      'Income tax return filing by FBR registered consultants. Flat PKR 5,000 for salaried individuals, custom quotes for businesses.',
    canonical: TAX_URL,
    keywords:
      'income tax filing Pakistan, income tax return filing, FBR tax consultant Lahore, IRIS filing, salaried tax return Pakistan, become a filer Pakistan',
    image: DEFAULT_IMAGE,
    jsonLd: [
      webPage(
        TAX_URL,
        'Income tax filing for salaried individuals and businesses',
        'Income tax return preparation and filing by FBR registered consultants in Pakistan.'
      ),
      breadcrumbs([
        { name: 'Home', url: HOME_URL },
        { name: 'Income Tax Filing', url: TAX_URL },
      ]),
      service(TAX_URL, {
        areaServed: PK,
        fields: {
          name: 'Income Tax Filing for Salaried Individuals and Businesses',
          serviceType: 'Income tax return filing',
          description:
            'Income tax return preparation and filing with the Federal Board of Revenue, handled by FBR registered consultants. Covers IRIS registration for first time filers, salaried returns and business returns.',
          audience: {
            '@type': 'Audience',
            name: 'Salaried individuals and business owners in Pakistan',
          },
          offers: [
            {
              '@type': 'Offer',
              name: 'Salaried individual income tax return',
              description: 'One flat fee per return for salaried individuals',
              price: '5000',
              priceCurrency: 'PKR',
            },
          ],
        },
      }),
      faqPage(TAX_URL, TAX_FAQ),
      howTo(TAX_URL, 'How to file your income tax return with Axiom Square',
             'The four steps from a first call to a return filed and confirmed with FBR.', [
               { name: 'Free 15 Minute Call', text: 'We understand your situation before anything else happens.' },
               { name: 'Share Your Documents', text: 'A short checklist, nothing you cannot find in ten minutes.' },
               { name: 'We Prepare and Review', text: 'You see the return before it is filed, with rebates explained.' },
               { name: 'We File and Confirm', text: 'Submitted to FBR, with confirmation and a copy for your records.' },
             ]),
    ],
  },

  odoo: {
    title: 'Odoo FBR and PRA Integration in Pakistan | Axiom Square',
    description:
      'A compliance layer inside your existing Odoo. We build the connector that submits your invoices to FBR or PRA, sandbox tested before go live.',
    canonical: ODOO_URL,
    keywords:
      'Odoo FBR integration, Odoo PRA integration, Odoo Pakistan localization, Odoo digital invoicing, Odoo tax compliance Pakistan, Odoo connector FBR',
    image: DEFAULT_IMAGE,
    jsonLd: [
      webPage(
        ODOO_URL,
        'FBR and PRA integration for Odoo',
        'Custom connector development that makes an existing Odoo installation report invoices to FBR or PRA.'
      ),
      breadcrumbs([
        { name: 'Home', url: HOME_URL },
        { name: 'Odoo Integration', url: ODOO_URL },
      ]),
      service(ODOO_URL, {
        areaServed: PK,
        fields: {
          name: 'FBR and PRA Integration for Odoo',
          serviceType: 'Odoo FBR and PRA integration',
          description:
            'Connector development that adds Pakistani tax compliance to an existing Odoo installation, so Odoo invoices are submitted to FBR or PRA without replacing the sales and invoicing workflow already in place. Works with Odoo Community and Odoo Enterprise.',
          audience: {
            '@type': 'BusinessAudience',
            name: 'Businesses in Pakistan already running Odoo',
          },
        },
      }),
      faqPage(ODOO_URL, ODOO_FAQ),
      howTo(ODOO_URL, 'How to add FBR and PRA digital invoicing to an existing Odoo',
             'The four steps from scoping an existing Odoo install to a live connector.', [
               { name: 'Scoping Call', text: 'We review what you run today: version, modules, customizations, volume.' },
               { name: 'Technical Review', text: 'A closer look to confirm exactly what the integration needs to touch.' },
               { name: 'Build and Sandbox Test', text: 'The connector is built and tested against FBR and PRA sandbox environments.' },
               { name: 'Go Live and Hand Off', text: 'We support your first live submissions and hand over documentation.' },
             ]),
    ],
  },
};

/* ------------------------------------------------------------------ */
/* Internal linking: service page to guide library                     */
/* ------------------------------------------------------------------ */

const G = '/guides/';

export const RELATED_GUIDES = {
  home: [
    { href: G + 'fbr-digital-invoicing-guide.html', title: 'FBR digital invoicing, explained for business owners', blurb: 'What it is, who has to comply, and what changes on your invoice' },
    { href: G + 'connect-pos-to-pra-digital-invoicing.html', title: 'You have your PRA POS ID, what happens next', blurb: 'Setting up a Punjab service business, step by step' },
    { href: G + 'fbr-digital-invoicing-cost-penalties-deadlines.html', title: 'Cost, penalties and deadlines', blurb: 'What connecting costs and what happens if you are late' },
    { href: G + 'fbr-digital-invoicing-enforcement-2026.html', title: 'FBR enforcement in 2026, what changed', blurb: 'Deadlines, green channel removal and tighter audits' },
  ],
  fbr: [
    { href: G + 'fbr-digital-invoicing-guide.html', title: 'FBR digital invoicing in Pakistan, a plain guide', blurb: 'What it is, who has to comply, what changes on the invoice' },
    { href: G + 'connect-pos-to-fbr-digital-invoicing.html', title: 'How to connect your POS to FBR, step by step', blurb: 'Registration, sandbox testing, going live' },
    { href: G + 'fbr-digital-invoicing-cost-penalties-deadlines.html', title: 'Cost, penalties and deadlines, explained', blurb: 'What it costs and what late compliance leads to' },
    { href: G + 'why-fbr-invoices-get-rejected.html', title: 'Why FBR invoices get rejected', blurb: 'Based on FBR validation error codes, not guesswork' },
    { href: G + 'static-ip-fbr-digital-invoicing.html', title: 'Do you need a static IP for FBR digital invoicing?', blurb: 'Why FBR and PRAL require a whitelisted address' },
    { href: G + 'fbr-invoice-qr-code-explained.html', title: 'Why FBR invoices now need a QR code', blurb: 'What the code contains and who scans it' },
  ],
  pra: [
    { href: G + 'connect-pos-to-pra-digital-invoicing.html', title: 'You have your PRA POS ID, how to start submitting', blurb: 'For Punjab service businesses with a POS ID and access token' },
    { href: G + 'fbr-digital-invoicing-guide.html', title: 'FBR digital invoicing, explained', blurb: 'Useful if you also sell goods and need both systems' },
    { href: G + 'why-fbr-invoices-get-rejected.html', title: 'Why invoices get rejected', blurb: 'The validation mistakes that cause most rejections' },
    { href: G + 'fbr-digital-invoicing-cost-penalties-deadlines.html', title: 'Cost, penalties and deadlines', blurb: 'What digital invoicing compliance actually costs' },
  ],
  tax: [
    { href: G + 'fbr-digital-invoicing-enforcement-2026.html', title: 'FBR enforcement in 2026, what changed', blurb: 'Compliance deadlines and tighter audit selection' },
    { href: G + 'sro-288-2026-online-integration-of-businesses.html', title: 'SRO 288(I)/2026, the draft integration rules', blurb: 'What the draft proposes and what is not yet in force' },
    { href: G + 'fbr-digital-invoicing-cost-penalties-deadlines.html', title: 'Cost, penalties and deadlines, explained', blurb: 'What happens when compliance slips' },
    { href: G + 'fbr-digital-invoicing-guide.html', title: 'FBR digital invoicing, explained', blurb: 'Background reading if your business is coming into scope' },
  ],
  odoo: [
    { href: G + 'connect-pos-to-fbr-digital-invoicing.html', title: 'How to connect your system to FBR, step by step', blurb: 'Registration, tokens, sandbox testing, go live' },
    { href: G + 'connect-pos-to-pra-digital-invoicing.html', title: 'Connecting to PRA digital invoicing', blurb: 'The Punjab Revenue Authority side of the same job' },
    { href: G + 'fbr-sandbox-testing-scenarios-explained.html', title: 'FBR sandbox testing, explained', blurb: 'What the scenarios check and why they gate go live' },
    { href: G + 'static-ip-fbr-digital-invoicing.html', title: 'Do you need a static IP?', blurb: 'The networking prerequisite most integrations hit first' },
  ],
};
